import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './Guide.module.css';

/**
 * MCP 接入指南页。
 * 同一 MCP 端点（/mcp/）承载两套工具集，通过请求头 target 切换：
 * - 博客文档（默认，不传 target）：路由 /guide/doc
 * - 照片日记（target: photo，cyberpunk 画廊）：路由 /guide/photo
 *
 * 路由分层直达 + 条件渲染（同一时刻只挂载当前工具集的内容）；
 * tabs 右侧「复制给 AI」一键复制接入指令，用户只需向 AI 提供 API Key。
 */

type McpTarget = 'doc' | 'photo';

/** 从路由参数解析工具集（非法值回退 doc） */
function parseTarget(raw: string | undefined): McpTarget {
  return raw === 'photo' ? 'photo' : 'doc';
}

// ── 博客文档工具集：可复制的按需 SKILL ──
const DOC_SKILL_TEMPLATE = `---
name: ranxin-blog-doc
description: 按需调用 ranxin-view 博客文档 MCP。仅在用户要求创建/读取/更新/删除/还原/回滚/搜索博客文档时激活，平时不加载、不命中。触发词如"写一篇博客""更新某篇文档""删除那篇文档""还原文档""回滚版本"。
---

# ranxin-blog-doc

## When to Activate

当用户要求操作 ranxin-view 博客文档时激活，典型表达：
- 创建："写一篇博客"、"新增一篇文档"
- 读取："读一下某篇文档"、"那篇文档内容是什么"
- 更新："更新某篇文档"、"改一下标题/正文"
- 删除："删掉那篇文档"、"把某篇移到回收站"
- 还原："把回收站里的某篇还原"
- 回滚："把某篇回滚到版本 N"
- 搜索/列出："找一下讲 X 的文档"、"列出某分类的文档"

非这些场景不要加载本 MCP，避免常驻命中。

## 接入信息

- 端点：https://ranxin.cc/mcp/
- 传输：Streamable HTTP
- 鉴权：请求头 X-API-Key，值为用户的博客 MCP API Key（从 ranxin-control 统一鉴权中心获取）
- 工具集选择：不传 target 请求头即是博客文档工具（默认）

## 使用方式

以 Streamable HTTP + 自定义请求头方式连接该端点，然后按需调用以下工具：

- create_document：创建文档（标题/分类/正文，slug 自动生成）。分类必须从工具参数描述列出的 8 个预设分类中选择（web/system/tools/language/debug/article/ai-docs/life），禁止自创；分类以工具描述为准
- list_documents：列出文档摘要，可按分类/状态/关键词过滤
- search_documents：关键词搜索文档
- read_document：读取单篇文档完整正文
- update_document：更新文档字段，自动保存旧版本快照并 version 自增
- delete_document：软删除（进入回收站，可还原）
- restore_document：从回收站还原
- rollback_document：回滚到指定历史版本
- list_document_versions：列出历史版本，供回滚选择目标版本

## 注意事项

- slug 形如「分类/标题」，例如 ai-docs/my-doc，多次操作需保持一致。
- 删除是软删除，可用 restore_document 还原。
- 回滚前先 list_document_versions 获取合法的 target_version。
- 每次更新/回滚自动保存内容快照并生成审计日志，全程留痕。`;

// ── 照片日记工具集：可复制的按需 SKILL ──
const PHOTO_SKILL_TEMPLATE = `---
name: ranxin-blog-photo
description: 按需调用 ranxin-view 照片日记 MCP（cyberpunk 画廊）。仅在用户要求上传照片、写照片日记、查看或管理画廊时激活，平时不加载、不命中。触发词如"上传这张照片""写一篇照片日记""画廊里有什么""把那张照片下架"。
---

# ranxin-blog-photo

## When to Activate

当用户要求操作 cyberpunk 照片画廊 / 照片日记时激活，典型表达：
- 上传："把这张图传到画廊"、"发一张照片并配一段话"
- 写日记："给这张照片写一篇日记"、"记录一下今天的生活"
- 查看："画廊里现在有什么"、"看看最近的日记"
- 管理："把那张照片下架"、"删掉某条日记"、"改一下日记日期"、"调整画廊排序"

非这些场景不要加载本 MCP，避免常驻命中。

## 接入信息

- 端点：https://ranxin.cc/mcp/
- 传输：Streamable HTTP
- 鉴权：请求头 X-API-Key，值为用户的博客 MCP API Key（与博客文档工具集共用同一 Key）
- 工具集选择：必须携带请求头 target: photo，否则连接的是博客文档工具集

## 使用方式

以 Streamable HTTP + 自定义请求头方式连接该端点，然后按需调用以下工具：

- create_photo_upload：上传第一步，颁发 COS 预签名上传 URL（图片内容不经过对话）
- register_photo_diary：上传第二步，curl 直传 COS 成功后注册入库（cos_key 原样回传）
- list_photo_diaries：列出照片日记，可包含已下架/已删除项
- update_photo_diary：更新日记标题/正文/日期/排序/上下架
- delete_photo_diary：软删除（COS 对象保留）

## 上传图片的标准流程（两步直传）

1. 先调 create_photo_upload（filename + content_type）拿到 upload_url 与 cos_key；
2. 用命令执行工具直传：curl -X PUT -H "Content-Type: <content_type>" --data-binary "@<本地图片路径>" "<upload_url>"；
3. 调 register_photo_diary（cos_key 原样回传 + title/description/diary_date/width/height）完成入库。

## 注意事项

- 上传图片只有两步直传一种方式（create_photo_upload → curl 直传 → register_photo_diary），图片字节流不经过对话上下文，单图上限 50MB。
- description 是用户在画廊点击卡片打开时看到的日记正文，建议用第一人称写一段有画面感的短文。
- diary_date 可指定日记日期（YYYY-MM-DD），缺省为上传当天；width/height 建议一并登记，画廊 HUD 会展示真实分辨率。
- 删除是软删除，COS 对象保留；下架用 status=hidden，重新上架用 status=active。`;

// ── 「复制给 AI」接入指令（用户只需提供 API Key 即可让 AI 完成添加） ──

const DOC_ADD_PROMPT = `请把下面的 MCP 服务添加到我的 MCP 客户端（mcpServers 配置）。配置中的 "<你的 API Key>" 是占位符，需要我的 API Key——如果我还未提供，请先向我索取，拿到后再写入配置。

服务名称：ranxin 博客文档（blog documents）
接入方式：Streamable HTTP
端点：https://ranxin.cc/mcp/
请求头：
- X-API-Key: <你的 API Key>

客户端配置：
{
  "mcpServers": {
    "ranxin-blog-doc": {
      "type": "http",
      "transport": "streamable",
      "url": "https://ranxin.cc/mcp/",
      "headers": {
        "X-API-Key": "<你的 API Key>"
      }
    }
  }
}

可用工具：create_document（创建文档）、list_documents（列出文档）、search_documents（搜索）、read_document（读全文）、update_document（更新）、delete_document（软删）、restore_document（还原）、rollback_document（回滚）、list_document_versions（版本列表）。

添加完成后请调用 list_documents 验证连接是否成功，并告诉我结果。`;

const PHOTO_ADD_PROMPT = `请把下面的 MCP 服务添加到我的 MCP 客户端（mcpServers 配置）。配置中的 "<你的 API Key>" 是占位符，需要我的 API Key——如果我还未提供，请先向我索取，拿到后再写入配置。

服务名称：ranxin 照片日记（photo diaries，cyberpunk 画廊）
接入方式：Streamable HTTP
端点：https://ranxin.cc/mcp/
请求头：
- X-API-Key: <你的 API Key>
- target: photo

客户端配置：
{
  "mcpServers": {
    "ranxin-blog-photo": {
      "type": "http",
      "transport": "streamable",
      "url": "https://ranxin.cc/mcp/",
      "headers": {
        "X-API-Key": "<你的 API Key>",
        "target": "photo"
      }
    }
  }
}

可用工具：upload_photo_diary（上传图片并写日记，图片 base64 编码，单图上限 7MB）、list_photo_diaries（列出日记）、update_photo_diary（更新文案/日期/排序/上下架）、delete_photo_diary（软删除）。

添加完成后请调用 list_photo_diaries 验证连接是否成功，并告诉我结果。`;

// ── 工具清单（与 mcp-doc 工具定义保持同步） ──

const DOC_TOOLS: { name: string; desc: string }[] = [
  { name: 'create_document', desc: '创建一篇新博客文档（标题/分类/正文，slug 自动生成）。分类须从 8 个预设分类中选择，见下方分类参考' },
  { name: 'list_documents', desc: '列出文档摘要列表，可按分类/状态过滤或关键词搜索' },
  { name: 'search_documents', desc: '按关键词模糊搜索文档，返回标题与 slug' },
  { name: 'read_document', desc: '读取单篇文档的完整 Markdown 正文' },
  { name: 'update_document', desc: '更新文档字段，自动保存旧版本快照并 version 自增' },
  { name: 'delete_document', desc: '软删除文档（进入回收站，可还原）' },
  { name: 'restore_document', desc: '从回收站还原已软删除的文档' },
  { name: 'rollback_document', desc: '将文档回滚到指定历史版本' },
  { name: 'list_document_versions', desc: '列出文档历史版本列表，供回滚选择目标版本' },
];

const PHOTO_TOOLS: { name: string; desc: string }[] = [
  { name: 'create_photo_upload', desc: '上传两步直传第一步：颁发 COS 预签名上传 URL（图片字节流不经过对话上下文，≤50MB）' },
  { name: 'register_photo_diary', desc: '上传两步直传第二步：注册已直传的图片并写入日记（cos_key + 文案元数据）' },
  { name: 'list_photo_diaries', desc: '列出照片日记记录，可包含已下架/已删除项' },
  { name: 'update_photo_diary', desc: '更新日记标题/正文/日期/排序/上下架，未传字段保持不变' },
  { name: 'delete_photo_diary', desc: '软删除照片日记（COS 对象保留，可恢复）' },
];

// 预设文档分类（SSOT 为 mcp-doc 服务 create_document 工具参数描述，此处为展示副本）
const CATEGORIES: { slug: string; desc: string; examples: string }[] = [
  { slug: 'web', desc: '前端具体技术/框架/库', examples: 'Vue3、React、Webpack、Vite、CSS、Axios' },
  { slug: 'system', desc: '后端/系统级', examples: 'NodeJs、KoaJs、Rust/Axum、Log4js' },
  { slug: 'tools', desc: '开发工具', examples: 'Git、Yarn、NPM、SSH-Keygen' },
  { slug: 'language', desc: '编程语言基础', examples: 'Rust 基础、TypeScript 语法' },
  { slug: 'debug', desc: '调试踩坑', examples: 'Live2D Widget 踩坑、Bug 复盘' },
  { slug: 'article', desc: '通用技术文章/方法论/实践指南', examples: 'VSCode 插件开发、RPC 方案、HTTP→HTTPS' },
  { slug: 'ai-docs', desc: 'AI 使用相关', examples: 'Skill 优化、提示词、AI 辅助设计' },
  { slug: 'life', desc: '生活记录', examples: '日常随想、生活日常、兴趣' },
];

// ── 共享 UI 片段 ──

function ToolTable({ tools }: { tools: { name: string; desc: string }[] }) {
  return (
    <div className={styles.toolTable}>
      {tools.map((t) => (
        <div className={styles.toolRow} key={t.name}>
          <code className={styles.toolName}>{t.name}</code>
          <span className={styles.toolDesc}>{t.desc}</span>
        </div>
      ))}
    </div>
  );
}

// ── 博客文档指南内容（/guide/doc） ──

function DocGuideContent() {
  return (
    <>
      <section className={styles.block}>
        <h2 className={styles.blockTitle}>1 · 服务端点</h2>
        <p className={styles.blockText}>
          博客文档 MCP 服务采用 Streamable HTTP 传输，暴露于当前站点 <code>/mcp/</code> 路径。
          <b>不传 <code>target</code> 请求头</b>（或传其他值）连接到的即是本工具集：
        </p>
        <pre className={styles.code}>{`MCP_ENDPOINT = https://ranxin.cc/mcp/`}</pre>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>2 · 鉴权方式</h2>
        <p className={styles.blockText}>
          服务使用统一 API Key 鉴权。请到 ranxin-control 统一鉴权中心的「API Key 管理」
          创建/获取你的 API Key，并在每次请求头携带 <code>X-API-Key</code>。
          留痕的操作者即是你为该 Key 设置的客户端名。
        </p>
        <pre className={styles.code}>{`X-API-Key: <你的 API Key>`}</pre>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>3 · 接入方式 A：常驻 mcpServers 配置</h2>
        <p className={styles.blockText}>
          若你希望 MCP 始终可用（AI 可直接命中工具），在支持 MCP 的客户端中注册该服务：
        </p>
        <pre className={styles.code}>
{`{
  "mcpServers": {
    "ranxin-blog-doc": {
      "type": "http",
      "transport": "streamable",
      "url": "https://ranxin.cc/mcp/",
      "headers": {
        "X-API-Key": "<你的 API Key>"
      }
    }
  }
}`}
        </pre>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>4 · 接入方式 B：按需 SKILL（推荐）</h2>
        <p className={styles.blockText}>
          若你希望 MCP 只在需要操作博客文档时才被调用，而不是常驻命中，
          请将下面的 <code>SKILL.md</code> 复制到你的 skill 库。
          该 SKILL 声明了何时激活、如何接入，以及可用工具清单。
        </p>
        <pre className={styles.code}>{DOC_SKILL_TEMPLATE}</pre>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>5 · 可用工具</h2>
        <ToolTable tools={DOC_TOOLS} />
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>6 · 文档分类参考</h2>
        <p className={styles.blockText}>
          创建文档时 <code>category</code> 必须从以下 8 个预设分类中选择，禁止自创分类名。
          分类的唯一事实来源是 <code>create_document</code> 工具参数描述（分类调整后自动对所有客户端生效），本表为展示副本。
          不确定归属时优先选 <code>article</code> 作为兜底。
        </p>
        <ToolTable tools={CATEGORIES.map((c) => ({ name: c.slug, desc: `${c.desc}（如 ${c.examples}）` }))} />
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>7 · 注意事项</h2>
        <ul className={styles.notes}>
          <li>文档 <code>slug</code> 形如「分类/标题」，例如 <code>ai-docs/my-doc</code>，多个操作间需保持一致。</li>
          <li>创建文档只需提供标题、分类与正文，slug 由后端自动生成；分类必须从 8 个预设分类中选择。</li>
          <li>删除是软删除，文档会进入回收站，可通过 <code>restore_document</code> 还原。</li>
          <li>回滚前请先用 <code>list_document_versions</code> 获取合法的目标版本号。</li>
          <li>每次更新/回滚都会自动保存完整内容快照并生成审计日志，便于留痕追溯。</li>
        </ul>
      </section>
    </>
  );
}

// ── 照片日记指南内容（/guide/photo） ──

function PhotoGuideContent() {
  return (
    <>
      <section className={styles.block}>
        <h2 className={styles.blockTitle}>1 · 服务端点</h2>
        <p className={styles.blockText}>
          照片日记工具集与博客文档共用同一 Streamable HTTP 端点 <code>/mcp/</code>，
          但<b>必须</b>在请求头携带 <code>target: photo</code> 才能连接到本工具集：
        </p>
        <pre className={styles.code}>{`MCP_ENDPOINT = https://ranxin.cc/mcp/`}</pre>
        <pre className={styles.code}>{`target: photo`}</pre>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>2 · 鉴权方式</h2>
        <p className={styles.blockText}>
          服务使用统一 API Key 鉴权（与博客文档工具集共用同一 Key）。
          请到 ranxin-control 统一鉴权中心的「API Key 管理」
          创建/获取你的 API Key，并在每次请求头携带 <code>X-API-Key</code>。
          留痕的操作者即是你为该 Key 设置的客户端名，照片日记记录会额外标记来源为 <code>mcp</code>。
        </p>
        <pre className={styles.code}>{`X-API-Key: <你的 API Key>`}</pre>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>3 · 接入方式 A：常驻 mcpServers 配置</h2>
        <p className={styles.blockText}>
          若你希望 MCP 始终可用（AI 可直接命中工具），在支持 MCP 的客户端中注册该服务：
        </p>
        <pre className={styles.code}>
{`{
  "mcpServers": {
    "ranxin-blog-photo": {
      "type": "http",
      "transport": "streamable",
      "url": "https://ranxin.cc/mcp/",
      "headers": {
        "X-API-Key": "<你的 API Key>",
        "target": "photo"
      }
    }
  }
}`}
        </pre>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>4 · 接入方式 B：按需 SKILL（推荐）</h2>
        <p className={styles.blockText}>
          若你希望 MCP 只在需要时才被调用，而不是常驻命中，
          请将下面的 <code>SKILL.md</code> 复制到你的 skill 库。
          该 SKILL 声明了何时激活、如何接入，以及可用工具清单。
        </p>
        <pre className={styles.code}>{PHOTO_SKILL_TEMPLATE}</pre>
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>5 · 可用工具</h2>
        <ToolTable tools={PHOTO_TOOLS} />
      </section>

      <section className={styles.block}>
        <h2 className={styles.blockTitle}>6 · 日记撰写与注意事项</h2>
        <ul className={styles.notes}>
          <li><code>description</code> 是用户在画廊点击卡片打开时看到的日记正文，建议用第一人称写一段有画面感的短文。</li>
          <li>上传图片只有两步直传一种方式：<code>create_photo_upload</code> 颁发 URL → 命令执行工具 curl 直传 → <code>register_photo_diary</code> 注册；图片字节流不经过对话上下文，jpeg/png/webp/gif ≤50MB。</li>
          <li><code>diary_date</code> 与上传时间解耦，可指定任意日记日期（YYYY-MM-DD），缺省为上传当天。</li>
          <li>注册入库时服务端自动生成多档压缩变体（默认 <code>ratios=[100,40]</code> + webp：100=无损档，40=小图档），画廊列表自动加载小图、点开大图用高画质档；gif 等动图自动跳过保动画，MCP 无需传参。HTTP 接口可通过 <code>ratios</code>（1..=100 质量百分比，1..4 个）与 <code>webp</code> 参数自定义。</li>
          <li>建议同时登记 <code>width/height</code>，画廊 HUD 卡片会展示真实分辨率。</li>
          <li>删除是软删除，COS 对象保留；下架用 <code>status=hidden</code>，重新上架用 <code>status=active</code>。</li>
          <li>通过 MCP 上传的记录会在画廊与管理端标记来源为 <code>mcp</code>，与手工上传区分。</li>
        </ul>
      </section>
    </>
  );
}

export default function Guide() {
  const navigate = useNavigate();
  const { target: rawTarget } = useParams<{ target: string }>();
  const target = parseTarget(rawTarget);
  const isPhoto = target === 'photo';

  // 一键复制状态反馈：idle | done | failed
  const [copyState, setCopyState] = useState<'idle' | 'done' | 'failed'>('idle');

  const addPrompt = isPhoto ? PHOTO_ADD_PROMPT : DOC_ADD_PROMPT;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(addPrompt);
      setCopyState('done');
    } catch (e) {
      // 剪贴板不可用（非 HTTPS 环境/权限拒绝）时给出可见失败反馈，不静默吞掉
      console.warn('复制到剪贴板失败:', e);
      setCopyState('failed');
    }
    setTimeout(() => setCopyState('idle'), 2000);
  };

  const switchTarget = (t: McpTarget) => {
    if (t !== target) navigate(`/guide/${t}`);
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.kicker}>DOCUMENTATION</span>
          <h1 className={styles.title}>MCP 接入指南</h1>
          <p className={styles.subtitle}>
            同一 MCP 端点承载两套工具集：<b>博客文档</b>与<b>照片日记</b>（cyberpunk 画廊），
            通过请求头 <code>target</code> 切换。使用下方标签页切换对应接入文档，
            或直接访问 <code>/guide/doc</code>、<code>/guide/photo</code> 定位。
          </p>
        </div>

        {/* 工具集切换 Tabs + 一键复制 */}
        <div className={styles.tabsRow}>
          <div className={styles.targetTabs} role="tablist" aria-label="MCP 工具集切换">
            <button
              type="button"
              role="tab"
              aria-selected={!isPhoto}
              className={`${styles.targetTab} ${!isPhoto ? styles.targetTabActive : ''}`}
              onClick={() => switchTarget('doc')}
            >
              博客文档
              <span className={styles.targetTabHint}>默认</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isPhoto}
              className={`${styles.targetTab} ${isPhoto ? styles.targetTabActive : ''}`}
              onClick={() => switchTarget('photo')}
            >
              照片日记
              <span className={styles.targetTabHint}>target: photo</span>
            </button>
          </div>

          <button
            type="button"
            className={`${styles.copyBtn} ${copyState === 'done' ? styles.copyBtnDone : ''} ${copyState === 'failed' ? styles.copyBtnFailed : ''}`}
            onClick={handleCopy}
            title="复制本工具集的接入指令，粘贴给 AI 即可完成添加（仅需提供 API Key）"
          >
            {copyState === 'done' ? '✓ 已复制' : copyState === 'failed' ? '✗ 复制失败' : '⧉ 复制给 AI'}
          </button>
        </div>

        {/* 条件渲染：同一时刻只挂载当前工具集的内容（等价 v-if） */}
        {isPhoto ? <PhotoGuideContent /> : <DocGuideContent />}
      </div>
    </div>
  );
}
